import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Wordmark } from "../components/Brand";
import { AlertTriangle, CheckCircle2, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Evaluate() {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [ratings, setRatings] = useState({});
  const [q2, setQ2] = useState("");
  const [q3, setQ3] = useState("");
  const [q4, setQ4] = useState("");
  const [mismatch, setMismatch] = useState(false);
  const [mismatchNote, setMismatchNote] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const [already, setAlready] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(`eval-submitted-${token}`)) setAlready(true);
    axios.get(`${API}/public/evaluation/${token}`)
      .then((r) => setForm(r.data))
      .catch(() => setError("This evaluation link is invalid or has expired."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = mismatch
        ? { mismatch: true, mismatch_note: mismatchNote, website: honeypot }
        : { ratings, q2, q3, q4, website: honeypot };
      const { data } = await axios.post(`${API}/public/evaluation/${token}/submit`, payload);
      localStorage.setItem(`eval-submitted-${token}`, "1");
      setDone(mismatch ? "Thank you — we've flagged this link for review and will follow up. No feedback was recorded against this service." : (data.confirmation || "Thank you."));
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally { setBusy(false); }
  };

  const allAnswered = form && form.statements.every((_, i) => ratings[`s${i + 1}`]);

  return (
    <div className="min-h-screen bg-canvas" data-testid="evaluation-page">
      <div className="bg-navy text-white px-6 py-4">
        <Wordmark light />
        <span className="ml-3 text-[11px] font-bold uppercase tracking-[0.18em] text-white/60">Teacher Feedback</span>
      </div>
      <div className="max-w-2xl mx-auto px-5 py-10 space-y-6">
        {error && <div className="rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3 text-sm font-bold text-[#991B1B]" data-testid="eval-error">{error}</div>}

        {done && (
          <div className="card p-8 text-center space-y-3 fade-up" data-testid="eval-confirmation">
            <CheckCircle2 size={36} className="mx-auto text-[#4A7C59]" />
            <p className="font-serif font-bold text-xl text-navy">{done}</p>
          </div>
        )}

        {form && !done && (
          <form onSubmit={submit} className="space-y-6 fade-up">
            <div className="card p-6 space-y-3" data-testid="eval-intro-card">
              <h1 className="font-serif font-bold text-2xl text-navy">{form.title}</h1>
              <p className="text-sm text-[#57534E] leading-relaxed">{form.intro}</p>
              <p className="text-xs text-[#78716C] leading-relaxed border-t border-[#F3EFE6] pt-3" data-testid="eval-viewer-note">{form.viewer_note}</p>
            </div>

            <div className="card p-6" data-testid="eval-context-card">
              <div className="label">Service details (from the approved service record)</div>
              <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm mt-2">
                <div><dt className="text-[#78716C] text-xs font-bold uppercase">School</dt><dd className="font-bold text-navy">{form.school_name}</dd></div>
                <div><dt className="text-[#78716C] text-xs font-bold uppercase">Consultant / Coach</dt><dd className="font-bold text-navy">{form.consultant_name}</dd></div>
                <div><dt className="text-[#78716C] text-xs font-bold uppercase">School year</dt><dd className="font-bold text-navy">{form.school_year}</dd></div>
                <div><dt className="text-[#78716C] text-xs font-bold uppercase">Service date / session</dt><dd className="font-bold text-navy">{form.service_date} · {form.session_label}</dd></div>
                <div className="sm:col-span-2"><dt className="text-[#78716C] text-xs font-bold uppercase">Services provided</dt>
                  <dd className="font-bold text-navy">{form.service_types.join(" · ") || "—"}</dd></div>
              </dl>
              <button type="button" onClick={() => setMismatch(!mismatch)} data-testid="eval-mismatch-toggle"
                className="mt-4 text-xs font-bold text-[#92400E] flex items-center gap-1.5 hover:underline">
                <AlertTriangle size={13} /> These details do not look correct</button>
            </div>

            {already && !mismatch && (
              <div className="rounded-lg border border-[#FCD34D] bg-[#FFFBEB] px-4 py-3 text-sm text-[#92400E]" data-testid="eval-already-submitted">
                This browser has already submitted a response for this session. You may submit again, but please only do so if you have additional feedback.
              </div>
            )}

            {mismatch ? (
              <div className="card p-6 space-y-4" data-testid="eval-mismatch-form">
                <p className="text-sm text-[#57534E]">Tell us what's wrong and we'll correct the record. Submitting here records <b>no feedback</b> against this service.</p>
                <textarea className="input min-h-[100px]" value={mismatchNote} onChange={(e) => setMismatchNote(e.target.value)}
                  placeholder="What doesn't look right?" data-testid="eval-mismatch-note" />
                <div className="hidden" aria-hidden="true">
                  <input tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} name="website" />
                </div>
                <button className="btn-primary" disabled={busy} data-testid="eval-mismatch-submit">{busy ? "Sending…" : "Flag mismatch"}</button>
              </div>
            ) : (
              <>
                <div className="card p-6 space-y-6" data-testid="eval-q1-card">
                  <div>
                    <div className="label">Question 1 · Required</div>
                    <p className="font-serif font-bold text-lg text-navy">{form.rating_prompt}</p>
                  </div>
                  {form.statements.map((stmt, i) => (
                    <fieldset key={i} className="border-t border-[#F3EFE6] pt-4" data-testid={`eval-statement-${i + 1}`}>
                      <legend className="text-sm font-bold text-navy mb-2.5">{i + 1}. {stmt}</legend>
                      <div className="grid gap-1.5">
                        {form.rating_options.map((opt) => (
                          <label key={opt} className={`flex items-center gap-3 rounded-lg border px-3.5 py-2.5 text-sm cursor-pointer transition-colors ${
                            ratings[`s${i + 1}`] === opt ? "border-terracotta bg-[#FDF3EE] font-bold text-navy" : "border-[#E7E1D4] text-[#57534E] hover:border-terracotta/50"
                          }`} data-testid={`eval-s${i + 1}-${opt.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
                            <input type="radio" name={`s${i + 1}`} value={opt} className="accent-[#B85C37]"
                              checked={ratings[`s${i + 1}`] === opt}
                              onChange={() => setRatings({ ...ratings, [`s${i + 1}`]: opt })} />
                            {opt}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  ))}
                </div>

                <div className="card p-6 space-y-5" data-testid="eval-written-card">
                  <div className="rounded-lg border border-[#FCD34D] bg-[#FFFBEB] px-4 py-3 text-sm font-bold text-[#92400E]" data-testid="eval-privacy-line">
                    {form.privacy_line}
                  </div>
                  {[["q2", form.written_questions[0], q2, setQ2], ["q3", form.written_questions[1], q3, setQ3], ["q4", form.written_questions[2], q4, setQ4]].map(([key, q, val, set]) => (
                    <div key={key}>
                      <label className="label">{q} <span className="normal-case font-normal">(optional)</span></label>
                      <textarea className="input min-h-[80px]" value={val} onChange={(e) => set(e.target.value)} data-testid={`eval-${key}`} />
                    </div>
                  ))}
                  <div className="hidden" aria-hidden="true">
                    <input tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} name="website" />
                  </div>
                </div>

                <button type="submit" className="btn-primary w-full !py-3.5" disabled={busy || !allAnswered} data-testid="eval-submit-button">
                  {busy ? "Submitting…" : allAnswered ? "Submit feedback" : "Please answer every statement in Question 1"}
                </button>
                <p className="text-xs text-[#78716C] text-center flex items-center justify-center gap-1.5">
                  <Lock size={11} /> No account or login required. Your name and email are not collected.
                </p>
              </>
            )}
          </form>
        )}
        {!form && !error && !done && <div className="text-sm text-[#78716C] animate-pulse text-center py-16" data-testid="eval-loading">Loading evaluation…</div>}
      </div>
    </div>
  );
}
